import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import jsPDF from 'jspdf';
import { API_URL, GPU_API_URL } from '../config';

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Dashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    analysis: '',
    coverLetter: '',
    enhancedResume: ''
  });
  const [success, setSuccess] = useState(false);

  const [inputs, setInputs] = useState({
    resume: '',
    jobDescription: ''
  });

  const [results, setResults] = useState({
    analysis: null,
    enhancedResume: '',
    coverLetter: ''
  });

  const [editedResults, setEditedResults] = useState({
    enhancedResume: '',
    coverLetter: ''
  });

  const [loadingStates, setLoadingStates] = useState({
    analysis: false,
    coverLetter: false,
    enhancedResume: false
  });

  // Add new state for usage tracking and pricing
  const [usageStats, setUsageStats] = useState({
    analysisCount: 0,
    maxAnalysis: 5, // Free tier default
    currentPlan: 'Free'
  });
  
  // Add state for the API usage limit
  const [apiUsage, setApiUsage] = useState(null);
  
  // Fetch usage stats from API
  const fetchApiUsage = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/generate/usage`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Fetched API usage stats:', response.data);
      setApiUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch API usage stats:', err);
    }
  }, [token]);
  
  // Fetch usage stats on component mount and when image is generated
  useEffect(() => {
    fetchApiUsage();
    
    // Listen for imageGenerated events
    const handleImageGenerated = () => {
      console.log('Image generated, refreshing API usage stats');
      fetchApiUsage();
    };
    
    window.addEventListener('imageGenerated', handleImageGenerated);
    return () => window.removeEventListener('imageGenerated', handleImageGenerated);
  }, [fetchApiUsage]);

  const handleInputChange = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value
    });
  };

  const generateCoverLetter = async (resume_text, job_description) => {
    const MAX_RETRIES = 3;
    let retries = 0;

    const attemptRequest = async () => {
      try {
        setLoadingStates(prev => ({ ...prev, coverLetter: true }));
        const response = await axios.post(`${GPU_API_URL}/generate-cover-letter`, 
          {
            resume_text,
            job_description
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            withCredentials: false,
            timeout: 30000
          }
        );
        
        // Handle both possible response formats
        const coverLetterText = response.data.cover_letter || response.data;
        
        setResults(prev => ({
          ...prev,
          coverLetter: coverLetterText
        }));
        setEditedResults(prev => ({
          ...prev,
          coverLetter: coverLetterText
        }));

        // Remove premature success message
        return true;
      } catch (err) {
        console.error(`Cover letter generation error (attempt ${retries + 1}):`, err);
        
        // Only retry on network errors, not CORS errors
        if (retries < MAX_RETRIES && 
            (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') && 
            !err.response) {
          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000 * retries)); // Exponential backoff
          return attemptRequest();
        }

        let errorMessage = 'Failed to generate cover letter. ';
        
        if (err.code === 'ERR_NETWORK') {
          errorMessage += 'Cannot connect to the server. This might be a CORS issue - please ensure the API server allows requests from localhost:3000';
        } else if (err.code === 'ECONNABORTED') {
          errorMessage += 'Request timed out. Please try again.';
        } else if (err.response) {
          // The server responded with a status code outside of 2xx
          errorMessage += err.response.data?.message || 'Server error occurred.';
        } else if (err.request) {
          // The request was made but no response was received
          errorMessage += 'No response from server. Please check your connection.';
        } else {
          errorMessage += 'An unexpected error occurred.';
        }

        console.error('Detailed error:', err);
        setError({
          ...error,
          coverLetter: errorMessage
        });
        return false;
      }
    };

    try {
      return await attemptRequest();
    } finally {
      setLoadingStates(prev => ({ ...prev, coverLetter: false }));
    }
  };

  const pollTaskStatus = async (taskId, maxAttempts = 30, interval = 2000) => {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const response = await axios.get(`${GPU_API_URL}/tasks/${taskId}`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          withCredentials: false,
          timeout: 10000
        });

        console.log('Task status response:', response.data);

        // Check if the task is completed
        if (response.data.status === 'completed' || response.data.status === 'success') {
          console.log('Task completed. Full result:', response.data);
          // Extract the result from the correct path in the response
          const result = response.data.result || response.data.data || response.data;
          // If result is an array, take the first item
          const finalResult = Array.isArray(result) ? result[0] : result;
          return { success: true, data: finalResult };
        } else if (response.data.status === 'failed' || response.data.status === 'error') {
          throw new Error(response.data.error || response.data.message || 'Task failed');
        } else if (response.data.status === 'processing' || response.data.status === 'pending') {
          console.log(`Task still processing (attempt ${attempts + 1})`);
          await new Promise(resolve => setTimeout(resolve, interval));
          attempts++;
          continue;
        }
        
        throw new Error(`Unknown task status: ${response.data.status}`);
      } catch (err) {
        console.error('Error polling task status:', err);
        if (err.response?.status === 404) {
          throw new Error('Task not found. Please try submitting again.');
        }
        throw err;
      }
    }
    throw new Error('Polling timeout: Task took too long to complete');
  };

  const analyzeResume = async (resume_text, job_description) => {
    const MAX_RETRIES = 3;
    let retries = 0;

    const attemptRequest = async () => {
      try {
        setLoadingStates(prev => ({ ...prev, analysis: true }));
        
        // Call the batch analysis API
        const response = await axios.post(`${GPU_API_URL}/batch-resume-to-jobs-with-llm`, 
          {
            resume_text,
            job_descriptions: [job_description]
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            withCredentials: false,
            timeout: 30000
          }
        );

        console.log('Initial API response:', response.data);

        // Get task ID from response
        const taskId = response.data.task_id;
        if (!taskId) {
          throw new Error('No task ID received from server');
        }

        // Poll for results
        const pollResult = await pollTaskStatus(taskId);
        
        if (!pollResult.success) {
          throw new Error('Failed to get analysis results');
        }

        // Extract the analysis results
        const analysisResult = pollResult.data;
        console.log('Raw analysis result:', analysisResult);

        // Process the analysis results
        const processedResults = {
          matchScore: 0,
          keyFindings: [],
          skillsMatch: [],
          missingSkills: [],
          jobTitle: '',
          company: '',
          location: '',
          rawAnalysis: {},
          qualitative_analysis: {
            experience_relevance: 0,
            education_fit: 0,
            overall_match: 0,
            strengths: [],
            weaknesses: [],
            skills_present: [],
            skills_missing: []
          }
        };

        if (analysisResult && typeof analysisResult === 'object') {
          console.log('Processing analysis result...');

          // Extract the actual analysis data from the nested structure
          const analysis = analysisResult.results?.[0] || {};
          console.log('Extracted analysis data:', analysis);

          // Store raw analysis
          processedResults.rawAnalysis = analysis;

          // Extract match score from various possible fields
          const score = analysis.match_percentage || 
                       analysis.match_score * 100 || 
                       analysis.similarity_score || 
                       (analysis.similarity && analysis.similarity.score) ||
                       0;

          processedResults.matchScore = Math.round(score);
          console.log('Found score:', processedResults.matchScore);

          // Extract qualitative analysis data if available
          if (analysis.llm_analysis?.qualitative_analysis) {
            processedResults.qualitative_analysis = analysis.llm_analysis.qualitative_analysis;
            console.log('Found qualitative_analysis in llm_analysis:', processedResults.qualitative_analysis);
          } else if (analysis.qualitative_analysis) {
            processedResults.qualitative_analysis = analysis.qualitative_analysis;
            console.log('Found qualitative_analysis at root level:', processedResults.qualitative_analysis);
          }

          // Extract skills match directly from qualitative_analysis
          if (processedResults.qualitative_analysis?.skills_present?.length) {
            processedResults.skillsMatch = processedResults.qualitative_analysis.skills_present;
            console.log('Using skills_present from qualitative_analysis:', processedResults.skillsMatch);
          } else {
            processedResults.skillsMatch = 
              analysis.matching_skills ||
              analysis.matched_skills ||
              analysis.skills_match ||
              [];
            console.log('Using alternative skills match sources:', processedResults.skillsMatch);
          }
          
          // Extract missing skills directly from qualitative_analysis
          if (processedResults.qualitative_analysis?.skills_missing?.length) {
            processedResults.missingSkills = processedResults.qualitative_analysis.skills_missing;
            console.log('Using skills_missing from qualitative_analysis:', processedResults.missingSkills);
          } else {
            processedResults.missingSkills = 
              analysis.missing_skills ||
              analysis.skill_gaps ||
              analysis.missing_requirements ||
              [];
            console.log('Using alternative missing skills sources:', processedResults.missingSkills);
          }

          // Extract resume skills if available
          if (analysis.llm_analysis?.resume_skills) {
            processedResults.rawAnalysis.resume_skills = analysis.llm_analysis.resume_skills;
            console.log('Found resume skills in llm_analysis:', processedResults.rawAnalysis.resume_skills);
          } else if (analysis.resume_skills) {
            processedResults.rawAnalysis.resume_skills = analysis.resume_skills;
            console.log('Found resume skills at root level:', processedResults.rawAnalysis.resume_skills);
          }

          // Extract qualification matches and gaps from qualitative_analysis
          if (processedResults.qualitative_analysis?.qualification_matches?.length) {
            processedResults.rawAnalysis.qualification_matches = processedResults.qualitative_analysis.qualification_matches;
          } else {
            processedResults.rawAnalysis.qualification_matches = 
              analysis.qualification_matches || [];
          }
          
          if (processedResults.qualitative_analysis?.qualification_gaps?.length) {
            processedResults.rawAnalysis.qualification_gaps = processedResults.qualitative_analysis.qualification_gaps;
          } else {
            processedResults.rawAnalysis.qualification_gaps = 
              analysis.qualification_gaps || [];
          }

          // Extract improvement suggestions
          if (processedResults.qualitative_analysis?.improvement_suggestions?.length) {
            processedResults.rawAnalysis.improvement_suggestions = processedResults.qualitative_analysis.improvement_suggestions;
          } else {
            processedResults.rawAnalysis.improvement_suggestions = 
              analysis.improvement_suggestions ||
              analysis.llm_analysis?.improvement_suggestions ||
              [];
          }

          // Add fallback logic for improvement suggestions based on weaknesses
          if (!processedResults.rawAnalysis.improvement_suggestions.length && 
              processedResults.qualitative_analysis?.weaknesses?.length) {
            // Generate improvement suggestions based on weaknesses
            processedResults.rawAnalysis.improvement_suggestions = 
              processedResults.qualitative_analysis.weaknesses.map(weakness => 
                `Consider addressing: ${weakness}`);
            console.log('Generated improvement suggestions from weaknesses');
          }

          // Extract job details
          processedResults.jobTitle = 
            analysis.job_title || 
            analysis.llm_analysis?.job_title || 
            '';

          processedResults.company = 
            analysis.company || 
            analysis.company_name || 
            analysis.llm_analysis?.company || 
            '';

          processedResults.location = 
            analysis.location || 
            analysis.job_location || 
            analysis.llm_analysis?.location || 
            '';

          // Update state with all results
          console.log('Setting final processed results:', processedResults);
          setResults(prev => ({
            ...prev,
            analysis: processedResults
          }));

          return true;
        }

        return false;
      } catch (err) {
        console.error(`Resume analysis error (attempt ${retries + 1}):`, err);
        
        if (retries < MAX_RETRIES && 
            (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') && 
            !err.response) {
          retries++;
          await new Promise(resolve => setTimeout(resolve, 1000 * retries));
          return attemptRequest();
        }

        let errorMessage = 'Failed to analyze resume. ';
        
        if (err.code === 'ERR_NETWORK') {
          errorMessage += 'Cannot connect to the server. Please check your connection.';
        } else if (err.code === 'ECONNABORTED') {
          errorMessage += 'Request timed out. Please try again.';
        } else if (err.response) {
          errorMessage += err.response.data?.message || 'Server error occurred.';
        } else if (err.request) {
          errorMessage += 'No response from server. Please check your connection.';
        } else {
          errorMessage += err.message || 'An unexpected error occurred.';
        }

        console.error('Detailed error:', err);
        setError({
          ...error,
          analysis: errorMessage
        });
        return false;
      }
    };

    try {
      return await attemptRequest();
    } finally {
      setLoadingStates(prev => ({ ...prev, analysis: false }));
    }
  };

  const generateEnhancedResume = async (resume_text, job_description) => {
    const MAX_RETRIES = 3;
    let retries = 0;

    const attemptRequest = async () => {
      try {
        setLoadingStates(prev => ({ ...prev, enhancedResume: true }));
        
        // Call the external resume rewriting API
        const response = await axios.post(`${GPU_API_URL}/rewrite-resume`, {
          resume_text,
          job_description
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: false,
          timeout: 30000
        });

        console.log('Enhanced resume API response:', response.data);

        // Extract the enhanced resume from the response
        let enhancedResumeText = '';
        if (response.data && typeof response.data === 'object') {
          enhancedResumeText = response.data.enhanced_resume || 
                              response.data.rewritten_resume || 
                              response.data.optimized_resume ||
                              response.data.improved_resume || 
                              response.data.resume || '';
        } else if (typeof response.data === 'string') {
          enhancedResumeText = response.data;
        }

        if (enhancedResumeText) {
          setResults(prev => ({ ...prev, enhancedResume: enhancedResumeText }));
          setEditedResults(prev => ({ ...prev, enhancedResume: enhancedResumeText }));
          return true;
        } else {
          throw new Error('No enhanced resume text found in response');
        }
      } catch (err) {
        console.error(`Enhanced resume generation error (attempt ${retries + 1}):`, err);
        
        if (retries < MAX_RETRIES && 
           (err.code === 'ECONNABORTED' || 
            err.message.includes('timeout') || 
            err.message.includes('Network Error'))) {
          retries++;
          console.log(`Retrying enhanced resume generation (attempt ${retries})`);
          
          // Exponential backoff
          const delay = Math.pow(2, retries) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          return attemptRequest();
        } else {
          setError({
            ...error,
            enhancedResume: `Error generating enhanced resume: ${err.response?.data?.message || err.message || 'Unknown error'}`
          });
          return false;
        }
      }
    };

    try {
      return await attemptRequest();
    } finally {
      setLoadingStates(prev => ({ ...prev, enhancedResume: false }));
    }
  };

  // Update usage check to navigate to pricing page instead of showing dialog
  const checkUsageLimit = () => {
    if (usageStats.analysisCount >= usageStats.maxAnalysis) {
      navigate('/pricing');
      return false;
    }
    return true;
  };

  // Modify handleAnalyze to check usage
  const handleAnalyze = async () => {
    if (!inputs.resume || !inputs.jobDescription) {
      setError({
        analysis: 'Please provide both resume and job description',
        coverLetter: 'Please provide both resume and job description',
        enhancedResume: 'Please provide both resume and job description'
      });
      return;
    }

    // Check both local usage limit and API generation limit
    if (!checkUsageLimit()) {
      return;
    }
    
    // Check API generation limit
    if (apiUsage && apiUsage.used >= apiUsage.limit) {
      setError({
        analysis: 'You have reached your image generation limit. Please upgrade your plan to continue.',
        coverLetter: '',
        enhancedResume: ''
      });
      navigate('/pricing');
      return;
    }

    setError({
      analysis: '',
      coverLetter: '',
      enhancedResume: ''
    });

    // Show processing time message
    setError({
      analysis: 'Processing your request. This typically takes 30 seconds to 1 minute.',
      coverLetter: '',
      enhancedResume: ''
    });

    setActiveTab(0);
    
    try {
      const [analysisSuccess, coverLetterSuccess, enhancedResumeSuccess] = await Promise.all([
        analyzeResume(inputs.resume, inputs.jobDescription),
        generateCoverLetter(inputs.resume, inputs.jobDescription),
        generateEnhancedResume(inputs.resume, inputs.jobDescription)
      ]);

      // Only show success message if all tasks completed successfully
      if (analysisSuccess && coverLetterSuccess && enhancedResumeSuccess) {
        setUsageStats(prev => ({
          ...prev,
          analysisCount: prev.analysisCount + 1
        }));
        
        // Update server-side image generation counter too
        try {
          const response = await axios.post(
            `${API_URL}/generate/test-limit`,
            { count: 1 },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          console.log('Updated image generation count after analysis:', response.data);
        } catch (err) {
          console.error('Error updating image generation count:', err);
        }
        
        // Trigger API usage update
        const event = new Event('imageGenerated');
        window.dispatchEvent(event);

        // Show success message only after all tasks are complete
        setSuccess(true);
        // Clear the processing message
        setError({
          analysis: '',
          coverLetter: '',
          enhancedResume: ''
        });
      } else {
        // Show error message if any task failed
        setError({
          analysis: 'Some tasks failed to complete. Please try again.',
          coverLetter: '',
          enhancedResume: ''
        });
      }

      console.log(`Analysis: ${analysisSuccess ? 'Success' : 'Failed'}, Cover Letter: ${coverLetterSuccess ? 'Success' : 'Failed'}, Enhanced Resume: ${enhancedResumeSuccess ? 'Success' : 'Failed'}`);
    } catch (err) {
      console.error('Error during analyze process:', err);
      setError({
        analysis: 'An error occurred during the analysis process. Please try again.',
        coverLetter: '',
        enhancedResume: ''
      });
    }
  };

  const handleDownload = (content, type) => {
    // Create a new PDF document
    const doc = new jsPDF();
    
    // Set document properties
    const title = type === 'cover-letter' ? 'Cover Letter' : 'Resume';
    doc.setProperties({
      title: title,
      subject: title,
      creator: 'Resume Match & Enhance Tool'
    });
    
    // Define margins and formatting parameters
    const margin = 20; // Left and right margins
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (2 * margin); // Available width for content
    const footerHeight = 10;
    
    // Function to add header to each page
    const addHeader = (pageInfo) => {
      if (pageInfo > 1) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(title, margin, margin);
      }
    };
    
    // Function to add footer with page numbers
    const addFooter = (pageInfo, totalPages) => {
      const pageText = `Page ${pageInfo} of ${totalPages}`;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const textWidth = doc.getStringUnitWidth(pageText) * 8 / doc.internal.scaleFactor;
      const textX = (pageWidth - textWidth) / 2;
      doc.text(pageText, textX, pageHeight - 10);
    };
    
    // Add title on first page
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, margin + 5);
    
    // Add main content with proper text wrapping
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    
    // Split text into lines with proper width
    const splitTextIntoLines = (text) => {
      // First split by newlines to preserve intended line breaks
      const paragraphs = text.split('\n');
      let allLines = [];
      
      paragraphs.forEach(paragraph => {
        // Check if this line is a section title (all caps, short line)
        const isTitle = paragraph.trim().toUpperCase() === paragraph.trim() && 
                       paragraph.trim().length > 0 &&
                       paragraph.trim().length < 50;
        
        // For each paragraph, split into lines that fit within margins
        const lines = doc.splitTextToSize(paragraph, contentWidth);
        
        // Add the line with metadata about whether it's a title
        allLines = allLines.concat(lines.map(line => ({
          text: line,
          isTitle: isTitle
        })));
      });
      
      return allLines;
    };
    
    const textLines = splitTextIntoLines(content);
    
    let cursorY = margin + 25; // Starting position after title
    const lineHeight = 7;
    let currentPage = 1;
    
    // Calculate total pages
    let totalPages = 1;
    let tempY = cursorY;
    
    textLines.forEach(line => {
      if (tempY > pageHeight - margin - footerHeight) {
        totalPages++;
        tempY = margin + 15;
      }
      tempY += line.isTitle ? lineHeight * 1.5 : lineHeight;
    });
    
    // Add content with pagination
    textLines.forEach(line => {
      if (cursorY > pageHeight - margin - footerHeight) {
        addFooter(currentPage, totalPages);
        doc.addPage();
        currentPage++;
        addHeader(currentPage);
        cursorY = margin + 15;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
      }
      
      // Set appropriate style for titles
      if (line.isTitle) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        cursorY += lineHeight * 0.5; // Add some extra space before titles
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
      }
      
      doc.text(line.text, margin, cursorY);
      
      // Adjust spacing after the line
      cursorY += line.isTitle ? lineHeight * 1.5 : lineHeight;
      
      // Reset font settings after title
      if (line.isTitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
      }
    });
    
    // Add footer to last page
    addFooter(currentPage, totalPages);
    
    // Save the PDF
    doc.save(`${type}.pdf`);
  };

  // Add a reset function to restore original content
  const handleResetContent = (type) => {
    if (type === 'enhanced-resume') {
      setEditedResults(prev => ({
        ...prev,
        enhancedResume: results.enhancedResume
      }));
    } else if (type === 'cover-letter') {
      setEditedResults(prev => ({
        ...prev,
        coverLetter: results.coverLetter
      }));
    }
  };

  // Add a test function to simulate image generation
  const simulateImageGeneration = async () => {
    try {
      // First check if user has reached their limit
      if (apiUsage && apiUsage.used >= apiUsage.limit) {
        // Show error message
        setError({
          ...error,
          analysis: 'You have reached your image generation limit. Please upgrade your plan to continue.'
        });
        // Navigate to pricing page
        navigate('/pricing');
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/generate/test-limit`,
        { count: 1 },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      console.log('Simulated image generation:', response.data);
      
      // Trigger update
      const event = new Event('imageGenerated');
      window.dispatchEvent(event);
    } catch (err) {
      console.error('Error simulating image generation:', err);
    }
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: 6, 
        minHeight: '90vh',
        maxWidth: '1920px !important' // Ensure maximum width for very large screens
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#fff' }}>
          Resume Match & Enhance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {/* <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {usageStats.analysisCount} / {usageStats.maxAnalysis} analyses used
            </Typography> */}
            
            {apiUsage && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <Box component="span" sx={{ color: '#00ffea', fontWeight: 'bold' }}>
                  Analysis Generation:
                </Box> {apiUsage.used} / {apiUsage.limit} 
                {' '}<Box component="span" sx={{ fontSize: '0.8rem' }}>
                  (Reset: {new Date(apiUsage.nextReset).toLocaleDateString()})
                </Box>
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* <Button
              variant="outlined"
              size="small"
              onClick={simulateImageGeneration}
              sx={{
                color: '#00ffea',
                borderColor: '#00ffea',
                '&:hover': {
                  borderColor: '#00b8ff',
                  color: '#00b8ff'
                }
              }}
            >
              Test Image
            </Button> */} 
            
            <Button
              variant="outlined"
              onClick={() => navigate('/pricing')}
              sx={{
                color: '#00ffea',
                borderColor: '#00ffea',
                '&:hover': {
                  borderColor: '#00b8ff',
                  color: '#00b8ff'
                }
              }}
            >
              Upgrade Plan
            </Button>
          </Box>
        </Box>
      </Box>

      <Typography 
        variant="body2" 
        sx={{ 
          color: 'rgba(255, 255, 255, 0.7)',
          fontStyle: 'italic',
          mb: 3
        }}
      >
        Note: Please carefully use the enhanced resume and cover letter as reference for further editing as they are generated by AI and may contain inaccurate information.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper
            elevation={3}
            sx={{
              backgroundColor: '#0a0b0e',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              overflow: 'hidden',
              minHeight: '75vh'
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    color: '#00ffea'
                  }
                }
              }}
            >
              <Tab icon={<UploadFileIcon />} label="Input" />
              <Tab icon={<AnalyticsIcon />} label="Analysis" />
              <Tab icon={<DescriptionIcon />} label="Enhanced Resume" />
              <Tab icon={<EmailIcon />} label="Cover Letter" />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3} sx={{ height: 'calc(75vh - 48px)' }}>
                <Grid item xs={12} md={6} sx={{ height: '85%' }}>
                  <TextField
                    fullWidth
                    multiline
                    name="resume"
                    label="Paste Your Resume"
                    value={inputs.resume}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#141519',
                        height: '100%',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-inputMultiline': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        '&::-webkit-scrollbar': {
                          width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#1e1e1e'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#555'
                          }
                        }
                      },
                      height: '100%'
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ height: '85%' }}>
                  <TextField
                    fullWidth
                    multiline
                    name="jobDescription"
                    label="Paste Job Description"
                    value={inputs.jobDescription}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#141519',
                        height: '100%',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-inputMultiline': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        '&::-webkit-scrollbar': {
                          width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#1e1e1e'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#555'
                          }
                        }
                      },
                      height: '100%'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAnalyze}
                    disabled={loadingStates.analysis || loadingStates.coverLetter || loadingStates.enhancedResume}
                    sx={{
                      mt: 2,
                      py: 2,
                      px: 6,
                      fontSize: '1.1rem',
                      borderRadius: 50,
                      background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
                      color: '#000',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(90deg, #00b8ff 0%, #00ffea 100%)',
                      },
                      '& .MuiCircularProgress-root': {
                        color: '#000'
                      }
                    }}
                  >
                    {(loadingStates.analysis || loadingStates.coverLetter || loadingStates.enhancedResume) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        {loadingStates.analysis ? 'Analyzing...' : loadingStates.coverLetter ? 'Generating Cover Letter...' : 'Generating Enhanced Resume...'}
                      </Box>
                    ) : (
                      'Analyze & Enhance'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              {results.analysis ? (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ color: '#fff', mb: 3 }}>
                    Match Results
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card sx={{ backgroundColor: '#0a0b0e', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ color: '#fff', mb: 2 }}>
                            Match Score: {Math.round(results.analysis.matchScore)}%
                          </Typography>

                          {/* Match Scores */}
                          {results.analysis.qualitative_analysis && (
                            <Paper 
                              elevation={2} 
                              sx={{ 
                                p: 2, 
                                mt: 2, 
                                mb: 2,
                                background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                                color: 'white',
                                borderRadius: '8px'
                              }}
                            >
                              <Typography variant="h6" gutterBottom sx={{ 
                                color: 'white', 
                                textAlign: 'center',
                                fontWeight: 500,
                                mb: 2
                              }}>
                                Detailed Match Analysis
                              </Typography>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <Box sx={{ 
                                    textAlign: 'center',
                                    p: 1.5,
                                    borderRadius: '6px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}>
                                    <Typography variant="h4" sx={{ 
                                      color: 'white', 
                                      fontWeight: 600,
                                      mb: 0.5
                                    }}>
                                      {results.analysis.qualitative_analysis.experience_relevance ? 
                                        `${results.analysis.qualitative_analysis.experience_relevance}/10` : 
                                        "N/A"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.9)',
                                      fontWeight: 500
                                    }}>
                                      Experience Relevance
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={4}>
                                  <Box sx={{ 
                                    textAlign: 'center',
                                    p: 2,
                                    borderRadius: '8px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      background: 'rgba(255, 255, 255, 0.15)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}>
                                    <Typography variant="h2" sx={{ 
                                      color: '#4CAF50', 
                                      fontWeight: 700,
                                      mb: 0.5,
                                      lineHeight: 1,
                                      fontSize: '2.5rem'
                                    }}>
                                      {results.analysis.qualitative_analysis.overall_match ? 
                                        `${results.analysis.qualitative_analysis.overall_match}/10` : 
                                        "N/A"}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ 
                                      color: 'white',
                                      fontWeight: 500
                                    }}>
                                      Overall Match
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={4}>
                                  <Box sx={{ 
                                    textAlign: 'center',
                                    p: 1.5,
                                    borderRadius: '6px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      transform: 'translateY(-2px)'
                                    }
                                  }}>
                                    <Typography variant="h4" sx={{ 
                                      color: 'white', 
                                      fontWeight: 600,
                                      mb: 0.5
                                    }}>
                                      {results.analysis.qualitative_analysis.education_fit ? 
                                        `${results.analysis.qualitative_analysis.education_fit}/10` : 
                                        "N/A"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.9)',
                                      fontWeight: 500
                                    }}>
                                      Education Fit
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Paper>
                          )}

                          {/* Resume Skills */}
                          {results.analysis.rawAnalysis?.resume_skills && results.analysis.rawAnalysis.resume_skills.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                                Resume Skills
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {results.analysis.rawAnalysis.resume_skills.map((skill, idx) => (
                                  <Chip 
                                    key={idx}
                                    label={typeof skill === 'object' ? 
                                      (skill.skill_name && skill.skill_level) ? 
                                        `${skill.skill_name} (${skill.skill_level})` : 
                                        skill.skill_name || skill.skill_level || JSON.stringify(skill) : 
                                      skill}
                                    sx={{
                                      backgroundColor: '#1a1b1f',
                                      color: '#fff',
                                      border: '1px solid rgba(255, 255, 255, 0.2)'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                                Resume Skills
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Skills Present */}
                          {results.analysis.skillsMatch && results.analysis.skillsMatch.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Skills Present in Job Requirements
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {results.analysis.skillsMatch.map((skill, idx) => (
                                  <Chip 
                                    key={idx}
                                    label={typeof skill === 'object' ? 
                                      (skill.skill_name && skill.skill_level) ? 
                                        `${skill.skill_name} (${skill.skill_level})` : 
                                        skill.skill_name || skill.skill_level || JSON.stringify(skill) : 
                                      skill}
                                    sx={{
                                      backgroundColor: '#4CAF50',
                                      color: '#fff'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Skills Present in Job Requirements
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Missing Skills */}
                          {results.analysis.missingSkills && results.analysis.missingSkills.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Skills Missing from Job Requirements
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {results.analysis.missingSkills.map((skill, idx) => (
                                  <Chip 
                                    key={idx}
                                    label={typeof skill === 'object' ? 
                                      (skill.skill_name && skill.skill_level) ? 
                                        `${skill.skill_name} (${skill.skill_level})` : 
                                        skill.skill_name || skill.skill_level || JSON.stringify(skill) : 
                                      skill}
                                    sx={{
                                      backgroundColor: '#f44336',
                                      color: '#fff'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Skills Missing from Job Requirements
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Qualification Matches */}
                          {results.analysis.rawAnalysis?.qualification_matches && results.analysis.rawAnalysis.qualification_matches.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Qualification Matches
                              </Typography>
                              <List>
                                {results.analysis.rawAnalysis.qualification_matches.map((match, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemIcon>
                                      <CheckCircleOutlineIcon sx={{ color: '#4CAF50' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={match} sx={{ color: '#fff' }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Qualification Matches
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Qualification Gaps */}
                          {results.analysis.rawAnalysis?.qualification_gaps && results.analysis.rawAnalysis.qualification_gaps.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Qualification Gaps
                              </Typography>
                              <List>
                                {results.analysis.rawAnalysis.qualification_gaps.map((gap, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemIcon>
                                      <CheckCircleOutlineIcon sx={{ color: '#f44336' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={gap} sx={{ color: '#fff' }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Qualification Gaps
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Strengths */}
                          {results.analysis.qualitative_analysis?.strengths && results.analysis.qualitative_analysis.strengths.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Strengths
                              </Typography>
                              <List>
                                {results.analysis.qualitative_analysis.strengths.map((strength, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemIcon>
                                      <CheckCircleOutlineIcon sx={{ color: '#4CAF50' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={strength} sx={{ color: '#fff' }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 2 }}>
                                Strengths
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Weaknesses */}
                          {results.analysis.qualitative_analysis?.weaknesses && results.analysis.qualitative_analysis.weaknesses.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Weaknesses
                              </Typography>
                              <List>
                                {results.analysis.qualitative_analysis.weaknesses.map((weakness, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemIcon>
                                      <CheckCircleOutlineIcon sx={{ color: '#f44336' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={weakness} sx={{ color: '#fff' }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>
                                Weaknesses
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}

                          {/* Improvement Suggestions */}
                          {results.analysis.rawAnalysis?.improvement_suggestions && results.analysis.rawAnalysis.improvement_suggestions.length > 0 ? (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#00ffea', mb: 2 }}>
                                Improvement Suggestions
                              </Typography>
                              <List>
                                {results.analysis.rawAnalysis.improvement_suggestions.map((suggestion, idx) => (
                                  <ListItem key={idx}>
                                    <ListItemIcon>
                                      <CheckCircleOutlineIcon sx={{ color: '#00ffea' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={suggestion} sx={{ color: '#fff' }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ) : (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ color: '#00ffea', mb: 2 }}>
                                Improvement Suggestions
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                N/A
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No analysis available yet. Please submit your resume and job description first.
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              {results.enhancedResume ? (
                <Box sx={{ height: 'calc(75vh - 48px)', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#00ffea' }}>
                      Enhanced Resume
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleResetContent('enhanced-resume')}
                        sx={{
                          color: '#ff9800',
                          borderColor: '#ff9800',
                          '&:hover': {
                            borderColor: '#ed6c02',
                            color: '#ed6c02'
                          }
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleDownload(editedResults.enhancedResume || results.enhancedResume, 'enhanced-resume')}
                        sx={{
                          color: '#00ffea',
                          borderColor: '#00ffea',
                          '&:hover': {
                            borderColor: '#00b8ff',
                            color: '#00b8ff'
                          }
                        }}
                      >
                        Download as PDF
                      </Button>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    value={editedResults.enhancedResume || results.enhancedResume}
                    onChange={(e) => setEditedResults(prev => ({ ...prev, enhancedResume: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#141519',
                        height: '100%',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-inputMultiline': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        '&::-webkit-scrollbar': {
                          width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#1e1e1e'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#555'
                          }
                        }
                      },
                      flexGrow: 1
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>
                    You can edit this content before downloading. Changes are saved automatically.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No enhanced resume available yet. Please submit your resume and job description first.
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              {results.coverLetter ? (
                <Box sx={{ height: 'calc(75vh - 48px)', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#00ffea' }}>
                      Generated Cover Letter
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleResetContent('cover-letter')}
                        sx={{
                          color: '#ff9800',
                          borderColor: '#ff9800',
                          '&:hover': {
                            borderColor: '#ed6c02',
                            color: '#ed6c02'
                          }
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleDownload(editedResults.coverLetter || results.coverLetter, 'cover-letter')}
                        sx={{
                          color: '#00ffea',
                          borderColor: '#00ffea',
                          '&:hover': {
                            borderColor: '#00b8ff',
                            color: '#00b8ff'
                          }
                        }}
                      >
                        Download as PDF
                      </Button>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    value={editedResults.coverLetter || results.coverLetter}
                    onChange={(e) => setEditedResults(prev => ({ ...prev, coverLetter: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#141519',
                        height: '100%',
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      },
                      '& .MuiInputBase-inputMultiline': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        '&::-webkit-scrollbar': {
                          width: '8px'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#1e1e1e'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#555'
                          }
                        }
                      },
                      flexGrow: 1
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>
                    You can edit this content before downloading. Changes are saved automatically.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No cover letter available yet. Please submit your resume and job description first.
                </Typography>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={!!error.analysis || !!error.coverLetter || !!error.enhancedResume}
        autoHideDuration={6000}
        onClose={() => setError({
          analysis: '',
          coverLetter: '',
          enhancedResume: ''
        })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={error.analysis === 'Processing your request. This typically takes 30 seconds to 1 minute.' ? 'info' : 'error'} 
          onClose={() => setError({
            analysis: '',
            coverLetter: '',
            enhancedResume: ''
          })}
        >
          {error.analysis || error.coverLetter || error.enhancedResume}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Analysis completed successfully! Check the Analysis, Enhanced Resume, and Cover Letter tabs to view your results.
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Dashboard; 